
import { API_STATUS } from "core-lib/constants/network";
import useAutoCompleteWithRGC from "core-lib/hooks/address/useAutoCompleteWithRGC";
import {
  Address,
  AddressKeys,
  AddressStatusType,
} from "core-lib/types/Address";
import { DictType } from "core-lib/types/Helper";
import { customToast } from "core-lib/utils/customToast";
import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import AnimatedImage from "core-lib/components/AnimatedImage";
import { SearchBar } from "ui-v2/components/SearchBar";
import { Modal } from "ui-v2/components/Modal/Modal";
import { Typography } from "ui-v2/components/Typography";
import { ModalTypeTypes } from "ui-v2/components/Modal/Modal";
import { GPS } from "ui-v2/icons";
import s from "./SelectLocation.module.scss";
import { UserLocation } from "core-lib/types/Location";
import { ArrowLeftRegular, LocationRegular } from "ui-v2/icons";
import BackButton from "ui-v2/components/BackButton";
import { Loader } from "ui-v2/components/Loader";

type SelectLocationProps = {
  show: boolean;
  onClose: () => void;
  onDone: (val: Address) => void;
  addressValidation?: AddressKeys[];
  customRootStyle?: DictType;
  autoFocus?: boolean;
  variant?: ModalTypeTypes;
  containerStyle?: React.CSSProperties;
  withCurrentLocationOption?: boolean;
  customEle?: any;
  locationV2RequestPayload?: UserLocation;
  isWeb?: boolean;
};

export const SelectLocation: FunctionComponent<SelectLocationProps> = ({
  show,
  onClose,
  onDone,
  addressValidation = ["pincode"],
  customRootStyle,
  autoFocus = true,
  variant = "page",
  containerStyle = {},
  withCurrentLocationOption = true,
  customEle = null,
  locationV2RequestPayload = null,
  isWeb = false,
}) => {
  const {
    query,
    updateQuery,
    suggestions,
    selectPlaceId,
    fetch,
    address,
    reset,
    suggestionAPiStatus,
  } = useAutoCompleteWithRGC({
    rgc: {
      enableRGCApi: true,
      enableFallbackRGCApi: true,
      locationV2RequestPayload,
    },
    addressValidation,
  });

  const [showResults, setShowResults] = useState(true);
  const [isAutoFocus, setIsAutoFocus] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef(null);

  const hideResults = () => {
    setShowResults(false);
  };

  useEffect(() => {
    if (address.status === AddressStatusType.Success) {
      onDone(address.data);
      setIsLoading(false);
      onClose();
    } else if (
      address.status === AddressStatusType.Failed ||
      address.status === AddressStatusType.Denied
    ) {
      setIsLoading(false);
      setShowResults(true);
      customToast({
        variant: "criticalPriority",
        label: "Location permission denied",
      });
    }
  }, [address]);

  useEffect(() => {
    setShowResults(true);
    setIsAutoFocus(true);
    updateQuery("");
    setIsAutoFocus(true);
    reset();
  }, [show]);

  return (
    <Modal
      head={{
        show: false,
      }}
      noPadding
      show={show}
      variant={variant}
      animation={{ type: "btt", timeout: "s" }}
      rootStyle={customRootStyle || {}}
      isWeb={isWeb}
    >
      <div className={s.container} style={containerStyle}>
        <BackButton type="hidden" onBack={onClose} />
        <div className={s.input_container} ref={inputRef}>
          <SearchBar
            leftIcon={{
              show: true,
              icon: <ArrowLeftRegular color="tertiary" />,
              onClick: () => {
                onClose();
              },
            }}
            mode="light"
            variant="large"
            autoFocus={autoFocus}
            value={query}
            isDisabled={isLoading}
            onChange={(e) => {
              updateQuery(e.target.value);
            }}
            placeholder="Search for areas, street name..."
            onCrossClick={() => updateQuery("")}
          />
        </div>
        {withCurrentLocationOption && (
          <div
            className={s.current_location}
            onClick={() => {
              setIsLoading(true);
              hideResults();
              fetch();
              setIsAutoFocus(false);
            }}
            aria-hidden
          >
            <div className={s.icon_container}>
              <GPS width={24} height={24} color="brand" />
            </div>
            <div className={s.text_container}>
              <div className={s.text_section}>
                <Typography
                  variant="label_3_medium"
                  label="Use Current location"
                  color="primary"
                />
                <Typography
                  variant="tiny_medium"
                  label="Using GPS"
                  color="secondary"
                />
              </div>
              {isLoading && (
                <div className={s.loader_section}>
                  <Loader color="primary" />
                </div>
              )}
            </div>
          </div>
        )}
        <div className={s.section_sep} />
        {showResults && (
          <div className={s.results_container}>
            <div className={s.heading_row}>
              {suggestions?.length > 0 && (
                <Typography
                  label="Nearby Locations"
                  variant="label_3_semi_bold"
                />
              )}
            </div>

            {suggestionAPiStatus === API_STATUS.in_progress && (
              <div className={s.shimmer}>
                {[...Array(5)].map((e, idx) => (
                  <div className={s.card} key={idx}>
                    <div className="cmn_shimmer" />
                    <div className={s.wrap}>
                      <div className="cmn_shimmer" />
                      <div className="cmn_shimmer" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {suggestionAPiStatus === API_STATUS.success &&
              suggestions.map((suggestion, index) => (
                <div
                  className={s.suggestions}
                  onClick={() => {
                    setShowResults(false);
                    setIsLoading(true);
                    inputRef.current.value = suggestion.primaryText;
                    selectPlaceId(suggestion);
                  }}
                  aria-hidden
                  key={index}
                >
                  <div className={s.icon_container}>
                    <LocationRegular width={20} height={20} />
                  </div>
                  <div className={s.text_section}>
                    <Typography
                      label={suggestion.primaryText}
                      variant="label_3_medium"
                    />
                    <Typography
                      label={suggestion.secondaryText}
                      color="secondary"
                      variant="body_4_regular"
                    />
                  </div>
                </div>
              ))}

            {!!query.length &&
              !suggestions.length &&
              suggestionAPiStatus === API_STATUS.success && (
                <div className={s.noAddressFound}>
                  <AnimatedImage src="https://parkplus-bkt-img.parkplus.io/production/fuel/public/Banner013_20230823231915535649.png" />
                  <div className={s.searchResult}>
                    <Typography
                      label="No results found for"
                      variant="label_1_medium"
                      style={{ textAlign: "center" }}
                    />
                    <Typography
                      label={`‘${query}’`}
                      variant="label_1_semi_bold"
                      style={{ textAlign: "center" }}
                    />
                  </div>

                  <Typography
                    label="Try searching for something else "
                    variant="body_3_medium"
                    color="secondary"
                    style={{ paddingTop: "6px" }}
                  />
                </div>
              )}

            {customEle}
          </div>
        )}
      </div>
    </Modal>
  );
};
